import React, {useContext, useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {getTranslation} from "../../helpers/getTranslation";
import {View, StyleSheet, TouchableOpacity} from "react-native";
import PtText from "../core/pt-text/pt-text";
import {styleVars} from "../../app-style-vars";
import {useNavigation} from "@react-navigation/native";
import {GetDuePaymentsRequest} from "../../model/GetDuePaymentsRequest";
import {getDuePayments} from "../../actions/duePaymentActions";
import PtActivityIndicator from "../core/pt-activity-indicator/pt-activity-indicator";
import PtCallout from "../core/pt-callout/pt-callout";
import PtDuePaymentSingle from "./pt-due-payment-single";
import PtDuePaymentsSearchFilterWrapper from "./pt-due-payments-search-filter-wrapper";
import UserContext from "../../helpers/UserContext";

const tomorrow = new Date().setDate(new Date().getDate() + 1);
const defaultMinimumDate = new Date(new Date(tomorrow).setFullYear(new Date(tomorrow).getFullYear() - 1));
const defaultMaximumDate = null; // future is allowed on due payments

export default function PtDuePaymentsPage(props:any) {
  /* labels */
  const noResultsText = props.noResultsText || getTranslation('due.payments.no.results.text') || 'No results are found. Please use the search for specific due payments or filter accordingly';
  const noResultsTextLimited = props.noResultsTextLimited || getTranslation('due.payments.limited.no.results.text') || 'No due payments found';
  const resultsFoundText = props.resultsFoundText || getTranslation('due.payments.results.found.text') || 'results found';
  const errorMessage = props.errorMessage || getTranslation('due.payments.orders.error.message') || 'Error occurred';
  const showAllDuePaymentsText = props.showAllDuePaymentsText ? props.showAllOrdersText : getTranslation('due.payments.show.all.due.payments.text') || 'Show all due payments';
  const loadMoreLabel = props.loadMoreLabel || getTranslation('due.payments.load.more.text');

  const limitedModeTitle = props.limitedModeTitle || getTranslation ('due.payments.limited.mode.title') || 'Your latest due payments';
  const limitedModeSubTitle = props.limitedModeSubTitle || getTranslation('due.payments.limited.mode.sub.title') || 'during the last 7 days';

  const loadingLabel = props.loadingLabel || getTranslation ('due.payments.loading.label') || 'Loading due payments';

  const limitedMode = props.limitedMode || false;
  const [limitedDuePayments, setLimitedDuePayments] = useState(null);
  const [limitedDuePaymentsCount, setLimitedDuePaymentsCount] = useState(null);

  const initialStartDate = props.startDate || new Date(Date.now() - 7 * 24 * 60 * 60 * 1000); // seven days ago
  const initialEndDate = props.endDate || new Date() // today
  const initialPageSize = props.pageSize || 10;
  const [pageSize, setPageSize] = useState(initialPageSize);
  const initialEntryType = props.entryTypes || 'DOC_01;DOC_03;DOC_09;DOC_11;DOC_14;DOC_17';
  const initialStatus = props.status || null;
  const initialMultiAccount = props.multiaccount || null;

  const hideSearchAndFilters = props.hideSearchAndFilters || false;
  const hideLoadMore = props.hideLoadMore || false;

  const minimumDate = props.minimumDate || defaultMinimumDate;
  const maximumDate = props.maximumDate || defaultMaximumDate;

  const navigation = useNavigation();
  const {userData, updateUserData} = useContext(UserContext);

  const impersonationOK = !userData.mustImpersonate || (userData.mustImpersonate && userData.impersonatingId !== null);

  const searchOptions = [
    {value: 'invoice', label: getTranslation('due.payments.search.options.invoice.no')},
    {value: 'amount', label: getTranslation('due.payments.search.options.amount')},
  ];

  const filtersOptions = {
    duePaymentStatus: {
      title: getTranslation('due.payments.filters.status.title'),
      choices: [
        {value: '', label: getTranslation('due.payments.filters.status.any')},
        {value: 'DUE', label: getTranslation('due.payments.filters.status.due')},
        {value: 'OVERDUE', label: getTranslation('due.payments.filters.status.overdue')},
      ]
    },
    duePaymentType: {
      title: getTranslation('due.payments.filters.type.title'),
      choices: [
        {value: 'DOC_01;DOC_03;DOC_09;DOC_11;DOC_14;DOC_17', label: getTranslation('due.payments.filters.type.any')},
        {value: 'DOC_14', label: getTranslation('due.payments.filters.type.pending.deposit')},
        {value: 'DOC_01', label: getTranslation('due.payments.filters.type.debit')},
        {value: 'DOC_11', label: getTranslation('due.payments.filters.type.credit')},
        {value: 'DOC_17', label: getTranslation('due.payments.filters.type.balance')},
        {value: 'DOC_09', label: getTranslation('due.payments.filters.type.acknowledgement')},
        {value: 'DOC_03', label: getTranslation('due.payments.filters.type.new')},
      ]
    },
    dueDate: {
      title: getTranslation('due.payments.filters.date.title'),
      startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), // seven days ago
      minDate: defaultMinimumDate,
      endDate: new Date(),
      maxDate: defaultMaximumDate,
      startDateLabel: getTranslation('due.payments.filters.date.from.label'),
      endDateLabel: getTranslation('due.payments.filters.date.to.label')
    }
  }

  /** action related stuff */
  const PTBaseSiteUID = props.baseSiteUID || userData.baseSiteUID;
  const PTSalesAreaDependantCustomerUid = props.salesAreaDependantCustomerUid || userData.salesAreaDependantCustomerUid;
  const currentLanguage = useSelector((state:any) => state.translationsReducer.currentLanguage);
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((state:any) => state.authReducer.isAuthenticated);
  const initialRequestSent = useSelector((state:any) => state.duePaymentsReducer.initialDataCollected);
  const loadingDuePayments = useSelector((state:any) => state.duePaymentsReducer.loading);
  const requestForDuePayments = useSelector((state:any) => state.duePaymentsReducer.request);
  const duePayments = useSelector((state:any) => state.duePaymentsReducer.duePayments);
  const duePaymentsCount = useSelector((state:any) => state.duePaymentsReducer.duePayments.length);
  const errors = useSelector((state:any) => state.duePaymentsReducer.errors);

  /** keep all search and filtering parameters in a state at top level
   * and pass them down to other components as props
   * when something is changed in child components, pass all the way to the top
   * via handler functions in props, then change state on top level
   * it should propagate back down to child component via props
   */
  const requestInitialState = {
    pageSize: null,
    offset: null,
    invoice: null,
    entryType: initialEntryType,
    dueDateFrom: initialStartDate,
    dueDate: initialEndDate,
    amount: null,
    quantity: null,
    status: initialStatus,
    multiAccount: false,
    customerIds: null,
    cnpjOrUid: null,
    emailAddress: null,
    searchType: 'invoice',
    searchTerm: null,
  }

  const [requestParams, setRequestParams] = useState(requestInitialState);

  useEffect(() => {
    if(isAuthenticated && impersonationOK) {
      if (!initialRequestSent && duePayments.length === 0) {
        const initialRequest = new GetDuePaymentsRequest(initialStartDate, initialEndDate, null, initialEntryType, initialStatus, initialMultiAccount);
        getDuePayments(dispatch, initialRequest, PTBaseSiteUID, PTSalesAreaDependantCustomerUid, currentLanguage)
          .then((response) => {
            if(limitedMode) {
              console.log('setting limitedDuePayments', response);
              setLimitedDuePayments(response?.duePayments);
              setLimitedDuePaymentsCount(response?.duePayments?.length);
            }
          })
          .finally(() => {
            console.log('the req after initial = ', requestForDuePayments);
          });
      }
    } else {
      setLimitedDuePayments(null);
    }
  },[initialRequestSent, isAuthenticated, impersonationOK])

  /** handlers for various props that have to be passed down the line and listened to back here */
  /** end date filter changed */
  const endDateChangedHandler = (data:any) => {
    console.log('due payments page, endDateChangedHandler, new value = ', data.newDate);
    setRequestParams({...requestParams, dueDate: data.newDate});
    console.log('due after setting = ', requestParams.dueDate);
  }
  /** start date filter changed */
  const startDateChangedHandler = (data:any) => {
    console.log('due payments page, startDateChangedHandler, new value = ', data.newDate);
    setRequestParams({...requestParams, dueDateFrom: data.newDate});
    console.log('after setting = ', requestParams, 'startDate= ', requestParams.dueDateFrom, ':');
  }
  /** due payments status filter changed */
  const duePaymentStatusFilterChangedHandler = (data:any) => {
    console.log('due payments page, duePaymentsStatusFilterChangedHandler, new value = ', data.newValue);
    setRequestParams({...requestParams, status: data.newValue})
  }
  /** due payments type filter changed */
  const duePaymentTypeFilterChangedHandler = (data:any) => {
    console.log('due payments page, duePaymentsTypeFilterChangedHandler, new value = ', data.newValue);
    setRequestParams({...requestParams, entryType: data.newValue})
  }
  /** search type changed */
  const searchTypeChangedHandler = (data:any) => {
    console.log('due payments page, searchTypeChangedHandler, new value = ', data.searchType);
    setRequestParams({...requestParams, searchType: data.searchType})
  }
  /** search term changed */
  const searchTermChangedHandler = (data:any) => {
    if(data.searchTerm == '') {data.searchTerm = null;}
    console.log('due payments page, searchTermChangedHandler, new value = ', data.searchTerm)
    setRequestParams({...requestParams, searchTerm: data.searchTerm})
  }
  /** search initiated */
  const searchHandler = () => {
    /** search should set due payments status to initial value,
     * entry type to initial value
     * time range to full span, page to initial, searchtype to searchterm and then send request */
    /** search request should not contain dates */
    setRequestParams({
      ...requestParams,
      dueDateFrom: minimumDate,
      dueDate: maximumDate,
      status: null,
      entryType: initialEntryType,
      [requestParams.searchType]:requestParams.searchTerm
    });
    const searchRequest = new GetDuePaymentsRequest(null, null, null, initialEntryType, initialStatus);
    searchRequest[requestParams.searchType] = requestParams.searchTerm;
    searchRequest.dueDateFrom = minimumDate;
    searchRequest.dueDate = maximumDate;
    getDuePayments(dispatch, searchRequest, PTBaseSiteUID, PTSalesAreaDependantCustomerUid, currentLanguage).finally(() => {
      setPageSize(initialPageSize);
    });
  }
  /** apply filters */
  const applyFiltersHandler = () => {
    /** apply filters should modify last request by setting date range, orderStatus1, search criteria and setting offset to null */
    setRequestParams({...requestParams});
    const filterRequest = requestForDuePayments;
    filterRequest[requestParams.searchType] = requestParams.searchTerm;
    filterRequest.dueDateFrom = requestParams.dueDateFrom;
    filterRequest.dueDate = requestParams.dueDate;
    filterRequest.status = requestParams.status;
    filterRequest.entryType = requestParams.entryType;
    getDuePayments(dispatch, filterRequest, PTBaseSiteUID, PTSalesAreaDependantCustomerUid, currentLanguage).finally(() => {
      setPageSize(initialPageSize);
    });
  }
  /** reset filters and search */
  const resetHandler = () => {
    /** reset should trigger initial request*/
    setRequestParams(requestInitialState);
    const resetRequest = new GetDuePaymentsRequest(initialStartDate, initialEndDate, null, initialEntryType, initialStatus, initialMultiAccount);
    getDuePayments(dispatch, resetRequest, PTBaseSiteUID, PTSalesAreaDependantCustomerUid, currentLanguage).finally(() => {
      setPageSize(initialPageSize);
    });
  }
  /** load more */
  /* TBD */

  /** go to orders page / due payments */
  const showAllDuePayments = () => {
    navigation.navigate('orderHistoryScreen', {activeTab: 'duePayments'});
  }

  const loadMoreDuePayments = () => {
    setPageSize(pageSize + initialPageSize);
  }

  return (
    <>
      {isAuthenticated ?
        <>
          <PtActivityIndicator show={loadingDuePayments} message={loadingLabel}/>
          <View style={{zIndex: 10}}>

            <PtDuePaymentsSearchFilterWrapper
              hide = {hideSearchAndFilters}
              searchOptions={searchOptions}
              filtersOptions={filtersOptions}
              requestParams={requestParams}
              duePaymentStatusFilterChangedHandler={duePaymentStatusFilterChangedHandler}
              duePaymentTypeFilterChangedHandler={duePaymentTypeFilterChangedHandler}
              startDateChangedHandler={startDateChangedHandler}
              endDateChangedHandler={endDateChangedHandler}
              applyFiltersHandler={applyFiltersHandler}
              resetHandler={resetHandler}
              searchTermChangedHandler={searchTermChangedHandler}
              searchTypeChangedHandler={searchTypeChangedHandler}
              searchHandler={searchHandler}
              duePaymentsCount={duePaymentsCount}
            />
          </View>
          <View>
            {!limitedMode && !errors ?
              <View style={[styles.ordersCountWrapper]}>
                {duePaymentsCount == 0 ?
                  <View style={[styles.noResultsContainer]}>
                    <PtText style={[styles.noResultsText]}>{noResultsText}</PtText>
                  </View>
                  :
                  <PtText style={[styles.duePaymentsCountText]}>{duePaymentsCount} {resultsFoundText}</PtText>
                }
              </View>
              : null
            }
            <View>
              { !limitedMode ?
                !errors ?
                  <>
                    {duePayments?.map((duePayment: any, idx: number) => (
                      idx < pageSize ?
                        <PtDuePaymentSingle duePayment={duePayment} key={`single-duePayment-${idx}`}></PtDuePaymentSingle>
                        : null
                    ))}
                  </>
                  :
                  <View>
                    <PtCallout type={'error'} message={errorMessage}/>
                  </View>
                :
                limitedMode ?
                  <>
                    <View>
                      <PtText style={[styles.limitedModeTitleText]}>{limitedModeTitle}</PtText>
                      <PtText style={[styles.limitedModeSubTitleText]}>{limitedModeSubTitle}</PtText>
                    </View>

                    {limitedDuePayments ?
                      <>
                        {limitedDuePayments?.map((duePayment: any, idx: number) => (
                          idx < pageSize ?
                            <PtDuePaymentSingle duePayment={duePayment} key={`single-duePayment-${idx}`} limitedMode></PtDuePaymentSingle>
                            : null
                        ))}
                      </>
                      : null
                    }

                    {limitedDuePaymentsCount == 0 ?
                      <View style={[styles.noResultsContainer]}>
                        <PtText style={[styles.noResultsText]}>
                          {noResultsTextLimited}
                        </PtText>
                      </View>
                      : null
                    }

                    <View style={[styles.showAllContainer]}>
                      <TouchableOpacity onPress={() => showAllDuePayments()}>
                        <PtText style={[styles.showAllText]}>
                          {showAllDuePaymentsText}
                        </PtText>
                      </TouchableOpacity>
                    </View>
                  </>
                  : null
              }
              {!hideLoadMore && !loadingDuePayments && (duePaymentsCount > pageSize) ?
                <View style={[styles.loadMoreContainer]}>
                  <TouchableOpacity onPress={() => loadMoreDuePayments()}>
                    <View style={[styles.loadMoreButton]}>
                      <PtText style={[styles.loadMoreText]}>{loadMoreLabel}</PtText>
                    </View>
                  </TouchableOpacity>
                </View>
                : null
              }
            </View>
          </View>
        </>
        :
        <View>
          <PtText style={{fontSize:styleVars.rem(.625), textAlign:'center'}}>{'Feature not available. Please login to view your due payments.'}</PtText>
        </View>
      }
    </>
  )
}

const styles = StyleSheet.create({
  limitedModeTitleText: {
    fontWeight: '600',
    fontSize: styleVars.rem(1.25),
  },
  limitedModeSubTitleText: {
    fontSize: styleVars.rem(.875),
    fontWeight: '600',
    color: styleVars.boschLightGray,
  },
  orderHeaderWrapper: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  orderNoWrapper: {
    flex: 1,
    maxWidth: '30%',
    borderWidth: 1,
    borderColor: styleVars.boschLightGray
  },
  orderDateWrapper: {
    flex: 1,
    maxWidth: '30%',
    borderWidth: 1,
    borderColor: styleVars.boschLightGray
  },
  orderStatusWrapper: {
    flex: 1,
    maxWidth: '30%',
    borderWidth: 1,
    borderColor: styleVars.boschLightGray
  },
  loadMoreContainer: {
    justifyContent: 'center',
    width: '100%'
  },
  loadMoreButton: {
    marginVertical: styleVars.rem(.5),
    paddingVertical: styleVars.rem(.5),
    paddingHorizontal: styleVars.rem(1),
    borderWidth: 1,
    borderColor: styleVars.boschDarkBlue,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#fff',
    flexGrow: 0
  },
  loadMoreText: {
    color: styleVars.boschDarkBlue
  },
  ordersCountWrapper: {
    paddingVertical: styleVars.rem(.5),
  },
  duePaymentsCountText: {
    fontSize: styleVars.rem(.875),
    fontWeight: '600',
  },
  noResultsContainer: {
    paddingHorizontal: styleVars.rem(.5),
    paddingVertical: styleVars.rem(.5),
  },
  noResultsText: {
    fontSize: styleVars.rem(.875),
    textAlign: 'justify'
  },
  showAllContainer: {
    paddingVertical: styleVars.rem(.5),
    paddingHorizontal: styleVars.rem(.5),
  },
  showAllText: {
    fontSize: styleVars.rem(.875),
    color: styleVars.boschDarkBlue,
    textAlign: 'right',
  }
})
